import { useState } from 'react';
import type { RunDetail, Turn } from '../../types/runExplorer';
import type { CollaborationDefinition } from '../../types/definition';
import { summarizeRun } from '../../utils/runTurnModel';
import { UserName } from '../ui/UserName';
import { formatClock } from './runExplorerShared';

/**
 * The Collaboration panel (task 1306): the run as a conversation. For the
 * selected turn it shows what came in (**Input**) and what went out
 * (**Output**), with a one-line context strip, beside the path, timeline and
 * journal, which stay the technical detail.
 *
 * Where each side comes from, first match wins:
 *
 *   Input  — the turn's user message when the entry step recorded one; else
 *            the body of the request the turn answered
 *            (`endpointResponses[i].requestBody`, the run-detail pairing rule),
 *            turn 0 falling back to `endpointRequest.body`; else the entry
 *            step's journal input.
 *   Output — the response the turn returned (`endpointResponses[i].response`);
 *            else, on the last turn, the collaboration's output; else the last
 *            step's result in the turn's journal.
 *
 * A threaded run shows the previous turn's output greyed above the input
 * ("earlier in this thread"). A single-shot run is the request and the response.
 */

/** Request-body keys that carry the caller's words, lifted out above the JSON. */
const MESSAGE_KEYS = ['message', 'text', 'prompt', 'question', 'query', 'content', 'input'] as const;

/** Output keys that carry the service's words. */
const REPLY_KEYS = ['reply', 'answer', 'response', 'text', 'message', 'result', 'output', 'content'] as const;

/** Lines past which a value collapses behind "Show all". */
const COLLAPSE_LINES = 12;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || (asRecord(value) !== null && Object.keys(value as object).length === 0);

/** The first non-empty string among `keys`, as [key, text]. */
const liftText = (value: unknown, keys: readonly string[]): [string, string] | null => {
  if (typeof value === 'string' && value.trim()) return ['', value];
  const record = asRecord(value);
  if (!record) return null;
  for (const key of keys) {
    const field = record[key];
    if (typeof field === 'string' && field.trim()) return [key, field];
  }
  return null;
};

const toText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2) ?? 'null';
  } catch {
    return String(value);
  }
};

/** The turn's input, by the rule above. */
const turnInput = (detail: RunDetail, turn: Turn, entryStepId: string | null): unknown => {
  const paired = detail.endpointResponses?.[turn.index]?.requestBody;
  if (!isEmpty(paired)) return paired;
  if (turn.index === 0 && !isEmpty(detail.endpointRequest?.body)) return detail.endpointRequest?.body;
  const entryStep = entryStepId ? turn.steps[entryStepId] : undefined;
  return entryStep?.entries[0]?.input ?? entryStep?.nodeData?.input ?? null;
};

/** The turn's output, by the rule above. */
const turnOutput = (detail: RunDetail, turn: Turn, isLast: boolean): unknown => {
  const paired = detail.endpointResponses?.[turn.index]?.response;
  if (!isEmpty(paired)) return paired;
  if (isLast && !isEmpty(detail.collaboration.output)) return detail.collaboration.output;
  for (const stepId of [...turn.path].reverse()) {
    const output = turn.steps[stepId]?.resultEntry?.output;
    if (!isEmpty(output)) return output;
  }
  return null;
};

/** A block of text or JSON that collapses past ~12 lines. */
function LongValue({ text, mono, testId }: { text: string; mono?: boolean; testId?: string }) {
  const [expanded, setExpanded] = useState(false);
  const lines = text.split('\n');
  const long = lines.length > COLLAPSE_LINES;
  const shown = long && !expanded ? `${lines.slice(0, COLLAPSE_LINES).join('\n')}\n…` : text;
  return (
    <div data-testid={testId}>
      <pre
        className={`whitespace-pre-wrap break-words text-sm text-gray-900 dark:text-gray-100 ${
          mono ? 'rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs dark:border-gray-700 dark:bg-gray-900' : 'font-sans'
        }`}
      >
        {shown}
      </pre>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          {expanded ? 'Show less' : 'Show all'}
        </button>
      )}
    </div>
  );
}

/** The record behind a lifted message, closed until asked for. */
function RecordToggle({ value, label }: { value: unknown; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        {open ? `Hide ${label}` : `Show ${label}`}
      </button>
      {open && (
        <div className="mt-1">
          <LongValue text={toText(value)} mono />
        </div>
      )}
    </div>
  );
}

/** A value as the caller's or the service's words when it has some, else as its record. */
function ValueView({ value, keys, recordLabel, testId }: { value: unknown; keys: readonly string[]; recordLabel: string; testId: string }) {
  if (isEmpty(value)) {
    return (
      <p data-testid={testId} className="text-sm italic text-gray-500 dark:text-gray-400">
        Nothing recorded
      </p>
    );
  }
  const lifted = liftText(value, keys);
  if (!lifted) return <LongValue text={toText(value)} mono testId={testId} />;
  const hasRecord = typeof value !== 'string';
  return (
    <div>
      <LongValue text={lifted[1]} testId={testId} />
      {hasRecord && <RecordToggle value={value} label={recordLabel} />}
    </div>
  );
}

interface RunCollaborationPanelProps {
  detail: RunDetail;
  turns: Turn[];
  turn: Turn | null;
  definition: CollaborationDefinition | null;
  entryStepId: string | null;
}

export function RunCollaborationPanel({ detail, turns, turn, definition, entryStepId }: RunCollaborationPanelProps) {
  if (!turn) return null;
  const isLast = turn.index === turns.length - 1;
  const previous = turn.index > 0 ? turns.find((t) => t.index === turn.index - 1) ?? null : null;
  const run = { ...detail.collaboration, ...detail.run };
  const runBy = detail.endpointRequest?.createdBy ?? run.createdBy ?? null;
  const environment = run.environment ?? null;

  const message = turn.userMessage;
  const input = turnInput(detail, turn, entryStepId);
  const output = turnOutput(detail, turn, isLast);
  const previousOutput = previous ? turnOutput(detail, previous, false) : null;
  const artifacts = summarizeRun([turn], definition).artifacts;

  const labelClass = 'text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';

  return (
    <section data-testid="run-collaboration-panel" aria-label="Collaboration" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Collaboration</h2>
        <p data-testid="run-collaboration-context" className="text-xs text-gray-500 dark:text-gray-400">
          Turn {turn.index + 1} of {Math.max(turns.length, 1)}
          {runBy && (
            <>
              {' · run by '}
              <UserName userId={runBy} />
            </>
          )}
          {environment && ` · ${environment}`}
          {` · started ${formatClock(turn.startedAt)}`}
          {turn.endedAt && ` · completed ${formatClock(turn.endedAt)}`}
        </p>
      </div>

      {previous && !isEmpty(previousOutput) && (
        <div data-testid="run-collaboration-earlier" className="rounded-md border border-dashed border-gray-200 p-3 opacity-60 dark:border-gray-700">
          <div className={labelClass}>Earlier in this thread · turn {previous.index + 1} output</div>
          <div className="mt-1">
            <ValueView value={previousOutput} keys={REPLY_KEYS} recordLabel="output record" testId="run-collaboration-earlier-output" />
          </div>
        </div>
      )}

      <div className="rounded-md bg-gray-50 p-3 dark:bg-gray-900/50">
        <div className={labelClass}>Input</div>
        <div className="mt-1">
          {message ? (
            <div>
              <LongValue text={message} testId="run-collaboration-input" />
              {!isEmpty(input) && <RecordToggle value={input} label="request body" />}
            </div>
          ) : (
            <ValueView value={input} keys={MESSAGE_KEYS} recordLabel="request body" testId="run-collaboration-input" />
          )}
        </div>
      </div>

      <div className="rounded-md bg-indigo-50/60 p-3 dark:bg-indigo-900/20">
        <div className={labelClass}>Output</div>
        <div className="mt-1">
          <ValueView value={output} keys={REPLY_KEYS} recordLabel="output record" testId="run-collaboration-output" />
        </div>
        {artifacts.length > 0 && (
          <p data-testid="run-collaboration-artifacts" className="mt-2 text-xs text-gray-600 dark:text-gray-300">
            Created: {artifacts.join(' · ')}
          </p>
        )}
      </div>
    </section>
  );
}
